import React, { useState, useEffect, useRef } from 'react';
import { Modal, Progress, Button } from 'antd';
import { ClockCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
// import {  postApi } from '../../../redux/apis';
import axios from 'axios';
import { apiConfig } from 'redux/apis';
import { ExpenseTransaction } from '../ExpenseTransactionMain';
import { toastText } from 'utils/utils';
import dayjs from 'dayjs';
const endPoint = process.env.REACT_APP_API_ENDPOINT;

interface DownloadAttachmentModalProps {
	visible: boolean;
	onCancel: () => void;
	selectedCount: number;
	selectedTxnIds?: string[];
	selectedTransactions?: ExpenseTransaction[];
	onDownloadComplete?: () => void;
	isHeaderChecked?: boolean;
	dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
	selectedAccounts?: string[];
	selectedCustomers?: string[];
	selectedClasses?: string[];
	selectedLocations?: string[];
	selectedEntityTypes?: string[];
	selectedLineBasedOn?: string[];
	selectedVendors?: string[];
}

const DownloadAttachmentModal: React.FC<DownloadAttachmentModalProps> = ({
	visible,
	onCancel,
	selectedCount,
	selectedTxnIds = [],
	selectedTransactions = [],
	onDownloadComplete,
	isHeaderChecked = false,
	dateRange = null,
	selectedAccounts = [],
	selectedCustomers = [],
	selectedClasses = [],
	selectedLocations = [],
	selectedEntityTypes = [],
	selectedLineBasedOn = [],
	selectedVendors = [],
}) => {
	const [progress, setProgress] = useState(0);
	const [timeRemaining, setTimeRemaining] = useState(0);
	const [showConfirmation, setShowConfirmation] = useState(true);
	const [isCompleted, setIsCompleted] = useState(false);
	const [zipFileName, setZipFileName] = useState('');
	const [progressMessage, setProgressMessage] = useState<string>('');
	const [hasClickedWaitHere, setHasClickedWaitHere] = useState(false);

	// Use refs to persist data across modal opens/closes
	const downloadStateRef = useRef({
		progress: 0,
		timeRemaining: 0,
		isProcessing: false,
		isCompleted: false,
		zipFileName: '',
		intervalId: null as NodeJS.Timeout | null,
		timerIntervalId: null as NodeJS.Timeout | null,
		requestId: null as string | null,
		startedAt: null as number | null,
		lastSelectedTxnIds: [] as string[], // Track last selection to detect changes
		estimatedTotalTime: null as number | null, // Store estimated total time for linear calculation
	});

	// Check if any selected transactions have attachable: true
	const hasAttachableTransactions = selectedTransactions.some(
		(transaction) => transaction.attachable
	);

	// Initialize basic state when modal opens
	useEffect(() => {
		if (visible && selectedCount > 0) {
			// Check if selection has changed
			const selectionChanged =
				selectedTxnIds?.length !== downloadStateRef.current.lastSelectedTxnIds?.length ||
				selectedTxnIds.some(
					(id, index) => id !== downloadStateRef.current.lastSelectedTxnIds[index]
				);

			const zipFileName = `attachments_${new Date()
				.toISOString()
				.slice(0, 10)}.zip`;

			// If selection changed, always reset to initial state
			if (selectionChanged) {
				// Clear any existing interval
				if (downloadStateRef.current.intervalId) {
					clearInterval(downloadStateRef.current.intervalId);
					downloadStateRef.current.intervalId = null;
				}
				if (downloadStateRef.current.timerIntervalId) {
					clearInterval(downloadStateRef.current.timerIntervalId);
					downloadStateRef.current.timerIntervalId = null;
				}

				// Reset to initial state for new download
				downloadStateRef.current = {
					progress: 0,
					timeRemaining: 0,
					isProcessing: false,
					isCompleted: false,
					zipFileName: zipFileName,
					intervalId: null,
					timerIntervalId: null,
					requestId: null,
					startedAt: null,
					lastSelectedTxnIds: [...selectedTxnIds],
					estimatedTotalTime: null,
				};

				// Update state
				setProgress(0);
				setTimeRemaining(0);
				setShowConfirmation(true);
				setIsCompleted(false);
				setZipFileName(zipFileName);
				setProgressMessage('');
				setHasClickedWaitHere(false);
			} else {
				// Selection hasn't changed - check if there's an active download
				if (
					downloadStateRef.current.isProcessing &&
					!downloadStateRef.current.isCompleted &&
					downloadStateRef.current.requestId
				) {
					// Restore state from ref only if download is still processing
					setProgress(downloadStateRef.current.progress);
					setTimeRemaining(downloadStateRef.current.timeRemaining);
					setIsCompleted(downloadStateRef.current.isCompleted);
					setZipFileName(downloadStateRef.current.zipFileName || zipFileName);
					setShowConfirmation(false);

					// Restart progress simulation if interval is not running
					if (!downloadStateRef.current.intervalId) {
						simulateProgress();
					}
				} else {
					// No active download - reset to initial state
					downloadStateRef.current = {
						...downloadStateRef.current,
						progress: 0,
						timeRemaining: 0,
						isProcessing: false,
						isCompleted: false,
						zipFileName: zipFileName,
						intervalId: null,
						requestId: null,
						startedAt: null,
						lastSelectedTxnIds: [...selectedTxnIds],
						estimatedTotalTime: null,
					};

					setProgress(0);
					setTimeRemaining(0);
					setShowConfirmation(true);
					setIsCompleted(false);
					setZipFileName(zipFileName);
					setProgressMessage('');
					setHasClickedWaitHere(false);
				}
			}
		} else if (!visible) {
			// Reset state when modal closes (but keep ref for active downloads)
			setProgress(0);
			setTimeRemaining(0);
			setShowConfirmation(true);
			setIsCompleted(false);
			setProgressMessage('');
			setHasClickedWaitHere(false);
			// Don't reset requestId here - let it persist if download is still processing
		}
	}, [visible, selectedCount, selectedTxnIds]);

	// Cleanup interval on unmount
	useEffect(() => {
		return () => {
			if (downloadStateRef.current.intervalId) {
				clearInterval(downloadStateRef.current.intervalId);
			}
			if (downloadStateRef.current.timerIntervalId) {
				clearInterval(downloadStateRef.current.timerIntervalId);
			}
		};
	}, []);

	// Listen for WebSocket notifications via CustomEvent
	useEffect(() => {
		const handleDownloadNotification = (event: Event) => {
			const notification = (event as CustomEvent<{
				requestId?: string;
				type?: string;
				status?: string | number;
				progress?: number;
				message?: string;
				processedCount?: number;
				totalCount?: number;
				zipS3Url?: string;
				fileName?: string;
				error?: string;
			}>).detail;
			const currentRequestId = downloadStateRef.current.requestId;


			// Match notification to this download's requestId
			if (!currentRequestId) {
				return;
			}

			if (notification.requestId !== currentRequestId) {
				return;
			}

			// Handle progress updates
			if (notification.type === 'attachmentDownloadProgress') {
				// Stop simulation when real progress updates arrive
				if (downloadStateRef.current.intervalId) {
					clearInterval(downloadStateRef.current.intervalId);
					downloadStateRef.current.intervalId = null;
				}

				const progressValue = notification.progress || 0;
				setProgress(progressValue);
				downloadStateRef.current.progress = progressValue;

				if (notification.message) {
					setProgressMessage(notification.message);
				}

				// Calculate time remaining based on actual progress and elapsed time
				const startedAt = downloadStateRef.current.startedAt || Date.now();
				const elapsed = Date.now() - startedAt;

				if (progressValue > 0 && progressValue < 100) {
					// Calculate new estimated total time based on current progress
					const newEstimatedTotalTime = (elapsed / progressValue) * 100;

					// Use exponential smoothing to stabilize the estimate and prevent flickering
					// This averages the new estimate with the previous one (70% old, 30% new)
					const currentEstimate = downloadStateRef.current.estimatedTotalTime;
					if (currentEstimate === null) {
						// First estimate - use it directly
						downloadStateRef.current.estimatedTotalTime = newEstimatedTotalTime;
					} else {
						// Smooth the estimate to prevent flickering
						downloadStateRef.current.estimatedTotalTime =
							currentEstimate * 0.7 + newEstimatedTotalTime * 0.3;
					}

					// Calculate remaining time linearly based on smoothed estimate
					const estimatedRemaining = Math.max(0,
						Math.ceil((downloadStateRef.current.estimatedTotalTime - elapsed) / 1000)
					);
					setTimeRemaining(estimatedRemaining);
					downloadStateRef.current.timeRemaining = estimatedRemaining;

					// Start linear timer for smooth countdown
					startLinearTimer();
				} else if (notification.processedCount && notification.totalCount) {
					// Fallback: Calculate based on processed/total counts if progress not available
					const remaining = notification.totalCount - notification.processedCount;
					if (notification.processedCount > 0) {
						const avgTimePerItem = elapsed / notification.processedCount;
						const estimatedRemaining = Math.ceil(
							(remaining * avgTimePerItem) / 1000
						);
						setTimeRemaining(estimatedRemaining);
						downloadStateRef.current.timeRemaining = estimatedRemaining;
					}
				}
			}

			// Handle completion or failure
			if (
				notification.type === 'attachmentDownload' ||
				(notification.requestId && notification.status !== undefined)
			) {
				// Check status - can be string or number (1 = success, 0 = failed)
				const status = notification.status;
				const isSuccess =
					status === 'finished' ||
					status === 'completed' ||
					status === '1' ||
					status === 1;

				if (isSuccess) {
					if (notification.zipS3Url) {
						
						handleDownloadFromS3(notification.zipS3Url, notification.fileName);
					} else {
						console.warn('DownloadAttachmentModal: Success status but no zipS3Url', notification);
					}
				} else if (
					status === 'failed' ||
					status === 'error' ||
					status === '0' ||
					status === 0
				) {
					handleDownloadError(
						notification.error || 'Download failed',
						notification.message || 'An error occurred while processing your download'
					);
				}
			}
		};

		window.addEventListener('download-notification', handleDownloadNotification as EventListener);

		return () => {
			window.removeEventListener(
				'download-notification',
				handleDownloadNotification as EventListener
			);
		};
	}, []);

	// Start actual download when user confirms
	const zipBlobRef = useRef<Blob | null>(null); // Store blob for later download

	// Start linear timer countdown that updates smoothly
	const startLinearTimer = () => {
		// Clear any existing timer interval
		if (downloadStateRef.current.timerIntervalId) {
			clearInterval(downloadStateRef.current.timerIntervalId);
			downloadStateRef.current.timerIntervalId = null;
		}

		// Only start if we have an estimated total time
		if (!downloadStateRef.current.estimatedTotalTime || !downloadStateRef.current.startedAt) {
			return;
		}

		const timerId = setInterval(() => {
			if (downloadStateRef.current.isCompleted) {
				clearInterval(timerId);
				downloadStateRef.current.timerIntervalId = null;
				return;
			}

			const startedAt = downloadStateRef.current.startedAt || Date.now();
			const elapsed = Date.now() - startedAt;
			const estimatedTotalTime = downloadStateRef.current.estimatedTotalTime;

			if (estimatedTotalTime) {
				// Calculate remaining time linearly
				const estimatedRemaining = Math.max(0,
					Math.ceil((estimatedTotalTime - elapsed) / 1000)
				);
				setTimeRemaining(estimatedRemaining);
				downloadStateRef.current.timeRemaining = estimatedRemaining;
			}
		}, 1000); // Update every second for smooth countdown

		downloadStateRef.current.timerIntervalId = timerId as unknown as NodeJS.Timeout;
	};

	// Simulate file processing with realistic progress based on transaction count
	const simulateProgress = () => {
		const startTime = Date.now();

		// Calculate estimated time based on transaction count
		// 1 transaction ≈ 4 attachments, 5 seconds per attachment
		const estimatedAttachmentsPerTransaction = 4;
		const secondsPerAttachment = 5;
		const totalEstimatedAttachments =
			selectedTxnIds?.length * estimatedAttachmentsPerTransaction;
		const totalEstimatedSeconds =
			totalEstimatedAttachments * secondsPerAttachment;
		const totalMs = totalEstimatedSeconds * 1000;

		// Minimum 5 seconds, maximum 120 seconds
		const finalTotalMs = Math.max(5000, Math.min(120000, totalMs));

		if (downloadStateRef.current.intervalId) {
			clearInterval(downloadStateRef.current.intervalId);
		}

		// Store estimated total time for linear calculation
		downloadStateRef.current.estimatedTotalTime = finalTotalMs;

		// Start linear timer for smooth countdown
		startLinearTimer();

		const id = setInterval(() => {
			if (downloadStateRef.current.isCompleted) {
				clearInterval(id);
				return;
			}
			const elapsed = Date.now() - startTime;
			let target = (elapsed / finalTotalMs) * 100;

			// More realistic progress curve
			if (target > 85) target = 85 + (target - 85) * 0.3;
			else if (target > 60) target = 60 + (target - 60) * 0.5;
			else if (target > 30) target = 30 + (target - 30) * 0.7;

			const next = Math.min(
				99, // Stop at 99% until actual download completes
				Math.max(downloadStateRef.current.progress, Math.floor(target))
			);

			// Update progress only - timer is handled by linear timer interval
			setProgress(next);
			downloadStateRef.current.progress = next;
		}, 200);
		downloadStateRef.current.intervalId = id as unknown as NodeJS.Timeout;
	};

	// Download from S3 when notification received
	const handleDownloadFromS3 = async (
		zipS3Url: string,
		fileName?: string
	): Promise<void> => {
		try {
			const companyId = localStorage.getItem('companyId');
			const config = await apiConfig();
			const accessToken = config.headers?.Authorization;
			const refreshToken = config.headers?.RefreshToken;

			const downloadResponse = await axios.post(
				`${endPoint}/attachments/download-from-s3`,
				{
					s3Url: zipS3Url,
					companyId: companyId,
				},
				{
					responseType: 'arraybuffer',
					headers: {
						Authorization: accessToken,
						RefreshToken: refreshToken,
						'Content-Type': 'application/json',
					},
				}
			);

			const blob = new Blob([downloadResponse.data], { type: 'application/zip' });
			zipBlobRef.current = blob;

			// Extract filename from response headers or use provided filename
			const contentDisposition =
				downloadResponse.headers?.['content-disposition'] || '';
			const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
			const serverFileName = decodeURIComponent(
				(fileNameMatch?.[1] || '').trim()
			);
			const finalFileName =
				serverFileName ||
				fileName ||
				zipFileName ||
				`attachments_${new Date().toISOString().slice(0, 10)}.zip`;

			setZipFileName(finalFileName);

			// Mark as complete and stop progress simulation
			if (downloadStateRef.current.intervalId) {
				clearInterval(downloadStateRef.current.intervalId);
				downloadStateRef.current.intervalId = null;
			}
			if (downloadStateRef.current.timerIntervalId) {
				clearInterval(downloadStateRef.current.timerIntervalId);
				downloadStateRef.current.timerIntervalId = null;
			}

			setIsCompleted(true);
			setProgress(100);
			setTimeRemaining(0);
			downloadStateRef.current.isCompleted = true;
			downloadStateRef.current.progress = 100;

			// Call completion callback if provided
			if (onDownloadComplete) {
				onDownloadComplete();
			}
		} catch (error: unknown) {
			handleDownloadError(error, 'Failed to download file from S3');
		}
	};

	// Handle download errors
	const handleDownloadError = (error: unknown, message: string) => {
		setIsCompleted(false);
		setShowConfirmation(true);
		downloadStateRef.current.isProcessing = false;

		if (downloadStateRef.current.intervalId) {
			clearInterval(downloadStateRef.current.intervalId);
			downloadStateRef.current.intervalId = null;
		}
		if (downloadStateRef.current.timerIntervalId) {
			clearInterval(downloadStateRef.current.timerIntervalId);
			downloadStateRef.current.timerIntervalId = null;
		}

		console.error('Download error:', error);
		toastText(message || 'Download failed', 'error');
	};

	const startDownload = async () => {
		try {
			setShowConfirmation(false);
			setHasClickedWaitHere(false);
			downloadStateRef.current.isProcessing = true;
			downloadStateRef.current.startedAt = Date.now();
			simulateProgress(); // Start estimated progress as fallback

			const companyId = localStorage.getItem('companyId');

			// Build request body based on isHeaderChecked (downloadAll mode)
			const body: {
				companyId: string | null;
				returnType: string;
				downloadType: string;
				downloadAll?: boolean;
				transactionIds?: string[];
				attachable?: boolean;
				fromDate?: string;
				toDate?: string;
				accountIds?: string[];
				customerIds?: string[];
				classIds?: string[];
				locationIds?: string[];
				entityTypes?: string[];
				lineBasedOn?: string[];
				vendorIds?: string[];
			} = {
				companyId,
				returnType: 'notification', // Request notification mode instead of direct S3 URL
				downloadType: 'all',
			};

			if (isHeaderChecked) {
				// Download ALL filtered transactions (downloadAll mode)
				body.downloadAll = true;
				body.transactionIds = []; // Empty array for "all"
				// Include current filter parameters
				body.fromDate = dateRange?.[0]?.format('YYYY-MM-DD');
				body.toDate = dateRange?.[1]?.format('YYYY-MM-DD');
				body.accountIds = selectedAccounts;
				body.customerIds = selectedCustomers;
				body.classIds = selectedClasses;
				body.locationIds = selectedLocations;
				body.entityTypes = selectedEntityTypes;
				body.lineBasedOn = selectedLineBasedOn;
				body.vendorIds = selectedVendors;
			} else {
				// Download only selected visible rows
				body.downloadAll = false;
				body.transactionIds = selectedTxnIds;
				body.attachable = hasAttachableTransactions;
			}

			const config = await apiConfig();
			const accessToken = config.headers?.Authorization;
			const refreshToken = config.headers?.RefreshToken;

			// Step 1: Trigger Lambda (non-blocking, returns requestId)
			const response = await axios.post(
				`${endPoint}/attachments/download-attachment-lambda`,
				body,
				{
					responseType: 'json',
					headers: {
						Authorization: accessToken,
						RefreshToken: refreshToken,
						'Content-Type': 'application/json',
					},
				}
			);

			// Check response structure - backend might return data nested or flat
			const responseData = response.data?.data || response.data;

			// Check if response contains requestId (new notification-based flow)
			if (responseData?.requestId && (responseData?.status === 'processing' || responseData?.status === 0)) {
				const newRequestId = responseData.requestId;
				downloadStateRef.current.requestId = newRequestId;
				downloadStateRef.current.lastSelectedTxnIds = [...selectedTxnIds]; // Update tracked selection

				// Wait for WebSocket notification - don't try to download yet
				// Progress will be updated via notifications
				return;
			}

			// Fallback: Handle old response format (direct S3 URL) for backward compatibility
			let zipS3Url = '';
			let fileName = '';

			if (responseData?.zipS3Url) {
				zipS3Url = responseData.zipS3Url;
				fileName = responseData.fileName || zipFileName;
			} else if (responseData?.s3Url) {
				zipS3Url = responseData.s3Url;
				fileName = responseData.fileName || zipFileName;
			} else if (typeof responseData === 'string') {
				zipS3Url = responseData;
				fileName = zipFileName;
			}

			// If we have S3 URL, download immediately (old flow)
			if (zipS3Url) {
				await handleDownloadFromS3(zipS3Url, fileName);
				return;
			}

			// If no requestId and no S3 URL, something went wrong
			if (!responseData?.requestId && !zipS3Url) {
				throw new Error('Invalid response from server');
			}
		} catch (error: any) {
			console.log('error', error);
			handleDownloadError(error.response.data.error, `${error.response.data.message}`);
		}
	};

	const handleCancel = () => {
		// Only clear interval if download is not processing
		// If processing, keep interval running so progress continues when modal reopens
		if (
			downloadStateRef.current.intervalId &&
			(!downloadStateRef.current.isProcessing || downloadStateRef.current.isCompleted)
		) {
			clearInterval(downloadStateRef.current.intervalId);
			downloadStateRef.current.intervalId = null;
		}
		if (
			downloadStateRef.current.timerIntervalId &&
			(!downloadStateRef.current.isProcessing || downloadStateRef.current.isCompleted)
		) {
			clearInterval(downloadStateRef.current.timerIntervalId);
			downloadStateRef.current.timerIntervalId = null;
		}

		// Only reset if not processing or completed
		// If processing, keep state so user can resume when modal reopens
		if (!downloadStateRef.current.isProcessing && !downloadStateRef.current.isCompleted) {
			// Reset ref completely for cancelled downloads
			downloadStateRef.current = {
				progress: 0,
				timeRemaining: 0,
				isProcessing: false,
				isCompleted: false,
				zipFileName: '',
				intervalId: null,
				timerIntervalId: null,
				requestId: null,
				startedAt: null,
				lastSelectedTxnIds: [],
				estimatedTotalTime: null,
			};

			// Reset state
			setProgress(0);
			setTimeRemaining(0);
			setIsCompleted(false);
			setShowConfirmation(true);
			setProgressMessage('');
			setHasClickedWaitHere(false);
		}

		onCancel();
	};

	const handleWaitHere = () => {
		// Keep the modal open to show progress
		console.log('User chose to wait for download completion');
		setHasClickedWaitHere(true);
	};

	const handleNotifyMe = () => {
		// Close modal but continue processing in background
		// State is preserved in ref and localStorage
		// When notification arrives, we can show browser notification or reopen modal
		console.log('User chose to be notified when ready');
		onCancel();

		// TODO: Show browser notification when download completes
		// This will be handled by the notification handler when status === 'finished'
	};

	const handleDownloadZip = () => {
		if (!zipBlobRef.current) return;

		const url = URL.createObjectURL(zipBlobRef.current);
		const link = document.createElement('a');
		link.href = url;
		link.download = zipFileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);

		// Close modal after download
		setTimeout(() => handleCancel(), 500);
	};

	const formatTime = (seconds: number): string => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
	};

	return (
		<Modal
			title={
				<div className={styles['modal-title']}>
					{showConfirmation
						? 'Download Attachments'
						: isCompleted
							? 'Download Ready'
							: 'Preparing Your Download'}
				</div>
			}
			open={visible}
			onCancel={handleCancel}
			footer={null}
			className={styles['download-modal']}
			width={500}
			centered
			closable={true}
			maskClosable={false}
		>
			<div className={styles['modal-content']}>
				{showConfirmation ? (
					// Confirmation Screen
					<>
						<p className={styles['modal-description']}>
							You are about to download attachment
							{selectedCount > 1 ? 's' : ''} from{' '}
							{isHeaderChecked
								? 'the filtered transactions'
								: 'the selected transactions'}
							.
						</p>

						<div className={styles['file-details']}>
							<div className={styles['file-info']}>
								<FileTextOutlined className={styles['file-icon']} />
								<span>
									{isHeaderChecked
										? 'All transactions selected within the filters'
										: `${selectedTxnIds.length} transaction${selectedTxnIds.length > 1 ? 's' : ''
										} selected`}
								</span>
							</div>
						</div>

						<div className={styles['modal-actions']}>
							<Button
								onClick={handleCancel}
								className={`${styles['cancel-button']}`}
							>
								Cancel
							</Button>
							<Button
								type="primary"
								onClick={startDownload}
								className={`${styles['download-button']}`}
							>
								Download Now
							</Button>
						</div>
					</>
				) : isCompleted ? (
					// Completion Screen
					<>
						<p className={styles['modal-description']}>
							Your download is ready! The ZIP file has been created
							successfully.
						</p>

						<div className={styles['completion-section']}>
							<div className={styles['zip-file-info']}>
								<FileTextOutlined className={styles['zip-icon']} />
								<div className={styles['zip-details']}>
									<span className={styles['zip-name']}>{zipFileName}</span>
								</div>
							</div>
						</div>

						<div className={styles['modal-actions']}>
							<Button
								onClick={handleCancel}
								className={`${styles['cancel-button']}`}
							>
								Close
							</Button>
							<Button
								type="primary"
								onClick={handleDownloadZip}
								className={`${styles['download-zip-button']}`}
							>
								Download ZIP
							</Button>
						</div>
					</>
				) : (
					// Progress Screen
					<>
						<p className={styles['modal-description']}>
							Packaging attachments into a ZIP file.
						</p>

						<div className={styles['progress-section']}>
							<div className={styles['progress-header']}>
								<span className={styles['progress-label']}>Progress</span>
								<span className={styles['progress-percentage']}>
									{Math.min(100, Math.max(0, Math.round(progress)))}%
								</span>
							</div>
							<Progress
								percent={Math.min(100, Math.max(0, progress))}
								strokeColor="#344735"
								trailColor="#f0f0f0"
								className={styles['progress-bar']}
								showInfo={false}
							/>
						</div>

						<div className={styles['time-remaining']}>
							<ClockCircleOutlined className={styles['time-icon']} />
							<span>{formatTime(timeRemaining)} remaining</span>
						</div>

						<div className={styles['file-details']}>
							<div className={styles['file-info']}>
								<FileTextOutlined className={styles['file-icon']} />
								<span>
									{progressMessage || 'Processing files...'}
								</span>
							</div>
						</div>

						<div className={styles['modal-actions']}>
							<Button
								onClick={handleNotifyMe}
								className={`${styles['notify-button']}`}
							>
								Notify Me When Ready
							</Button>
							{!hasClickedWaitHere && (
								<Button
									type="primary"
									onClick={handleWaitHere}
									className={`${styles['wait-button']}`}
								>
									Wait Here
								</Button>
							)}
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};

export default DownloadAttachmentModal;
